import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft, Star } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '../design';
import { useFeeProfile, useUpdateFeeProfile } from '../lib/feeProfiles';
import type { FeeProfileData } from '@sourcito/schemas';

interface FormState {
  name: string;
  isDefault: boolean;
  defaultReferralPct: string; // store as strings while editing
  defaultFbaPickPack: string;
  defaultStoragePerUnit: string;
  defaultPrepPerUnit: string;
  defaultFreightPctOfLanded: string;
  thresholdA: string;
  thresholdB: string;
  thresholdC: string;
  thresholdD: string;
}

function profileToForm(p: { name: string; isDefault: boolean; data: FeeProfileData }): FormState {
  return {
    name: p.name,
    isDefault: p.isDefault,
    defaultReferralPct: String(p.data.defaultReferralPct),
    defaultFbaPickPack: String(p.data.defaultFbaPickPack),
    defaultStoragePerUnit: String(p.data.defaultStoragePerUnit),
    defaultPrepPerUnit: String(p.data.defaultPrepPerUnit),
    defaultFreightPctOfLanded: String(p.data.defaultFreightPctOfLanded),
    thresholdA: String(p.data.roiThresholds.A),
    thresholdB: String(p.data.roiThresholds.B),
    thresholdC: String(p.data.roiThresholds.C),
    thresholdD: String(p.data.roiThresholds.D),
  };
}

function formToData(
  f: FormState,
  currentOverrides: FeeProfileData['categoryOverrides'],
  currentMarkup: number
): FeeProfileData {
  return {
    defaultReferralPct: Number(f.defaultReferralPct),
    defaultFbaPickPack: Number(f.defaultFbaPickPack),
    defaultStoragePerUnit: Number(f.defaultStoragePerUnit),
    defaultPrepPerUnit: Number(f.defaultPrepPerUnit),
    defaultFreightPctOfLanded: Number(f.defaultFreightPctOfLanded),
    defaultSupplierMarkupPct: currentMarkup,
    categoryOverrides: currentOverrides,
    roiThresholds: {
      A: Number(f.thresholdA),
      B: Number(f.thresholdB),
      C: Number(f.thresholdC),
      D: Number(f.thresholdD),
      F: 0,
    },
  };
}

export function FeeProfileEditPage() {
  const { id } = useParams({ from: '/fee-profiles/$id' });
  const { data: profile, isLoading, isError, error } = useFeeProfile(id);
  const update = useUpdateFeeProfile(id);
  const [form, setForm] = useState<FormState | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (profile && !form) {
      setForm(profileToForm(profile));
    }
  }, [profile, form]);

  if (isLoading || !form) {
    return (
      <div className="min-h-screen bg-canvas text-fg-primary flex items-center justify-center">
        <p className="text-sm text-fg-secondary">Loading fee profile…</p>
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="min-h-screen bg-canvas text-fg-primary flex items-center justify-center px-8">
        <Card className="border-negative/30 bg-negative/[0.06] max-w-md">
          <p className="text-sm text-negative">
            Failed to load: {(error as Error)?.message ?? 'unknown error'}
          </p>
          <Link to="/fee-profiles" className="text-sm text-accent-fg hover:underline">
            ← Back to profiles
          </Link>
        </Card>
      </div>
    );
  }

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSavedFlash(false);
  };

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-3xl mx-auto px-8 py-4 flex items-center gap-3">
          <Link
            to="/fee-profiles"
            className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
          >
            <ChevronLeft className="size-5" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-medium leading-tight">{profile.name}</h1>
              {profile.isDefault && (
                <Badge variant="accent" className="gap-1">
                  <Star className="size-3" /> Default
                </Badge>
              )}
              <Badge variant="neutral">v{profile.version}</Badge>
            </div>
            <p className="text-xs text-fg-tertiary mt-0.5">
              Edit fees, freight, and ROI thresholds.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-10 space-y-6">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const data = formToData(
              form,
              profile.data.categoryOverrides,
              profile.data.defaultSupplierMarkupPct ?? 0.5
            );
            const dataChanged =
              JSON.stringify(data) !== JSON.stringify(profile.data) || form.name !== profile.name;
            if (!dataChanged && form.isDefault === profile.isDefault) return;
            try {
              await update.mutateAsync({
                name: form.name !== profile.name ? form.name : undefined,
                data: JSON.stringify(data) !== JSON.stringify(profile.data) ? data : undefined,
                isDefault: form.isDefault !== profile.isDefault ? form.isDefault : undefined,
              });
              setSavedFlash(true);
            } catch {
              // surfaced via update.isError
            }
          }}
          className="space-y-6"
        >
          <Card>
            <CardHeader>
              <CardTitle>Basics</CardTitle>
            </CardHeader>
            <div className="space-y-4">
              <FormField label="Name" htmlFor="name" required>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                />
              </FormField>
              <label className="flex items-center gap-2 text-sm text-fg-primary cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isDefault}
                  disabled={profile.isDefault}
                  onChange={(e) => setField('isDefault', e.target.checked)}
                  className="size-4 rounded-xs border-border-default accent-[rgb(var(--accent-primary))]"
                />
                Make this the default profile
                {profile.isDefault && <span className="text-fg-tertiary">(already default)</span>}
              </label>
            </div>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>FBA fees</CardTitle>
              <CardDescription>Per-unit defaults; override per category later.</CardDescription>
            </CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Referral %" htmlFor="referral" helper="Decimal: 0.15 = 15%.">
                <Input
                  id="referral"
                  type="number"
                  step="0.001"
                  min="0"
                  max="1"
                  value={form.defaultReferralPct}
                  onChange={(e) => setField('defaultReferralPct', e.target.value)}
                />
              </FormField>
              <FormField label="Pick &amp; pack ($)" htmlFor="pickpack">
                <Input
                  id="pickpack"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.defaultFbaPickPack}
                  onChange={(e) => setField('defaultFbaPickPack', e.target.value)}
                />
              </FormField>
              <FormField label="Storage / unit ($)" htmlFor="storage">
                <Input
                  id="storage"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.defaultStoragePerUnit}
                  onChange={(e) => setField('defaultStoragePerUnit', e.target.value)}
                />
              </FormField>
              <FormField label="Prep / unit ($)" htmlFor="prep">
                <Input
                  id="prep"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.defaultPrepPerUnit}
                  onChange={(e) => setField('defaultPrepPerUnit', e.target.value)}
                />
              </FormField>
              <FormField
                label="Freight % of supplier cost"
                htmlFor="freight"
                helper="Decimal: 0.08 = 8%."
                className="sm:col-span-2"
              >
                <Input
                  id="freight"
                  type="number"
                  step="0.001"
                  min="0"
                  value={form.defaultFreightPctOfLanded}
                  onChange={(e) => setField('defaultFreightPctOfLanded', e.target.value)}
                />
              </FormField>
            </div>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ROI thresholds</CardTitle>
              <CardDescription>
                Decimal cutoffs: 0.50 = 50% landed-cost ROI. F is everything below D.
              </CardDescription>
            </CardHeader>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <FormField label="A ≥" htmlFor="ta">
                <Input
                  id="ta"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.thresholdA}
                  onChange={(e) => setField('thresholdA', e.target.value)}
                />
              </FormField>
              <FormField label="B ≥" htmlFor="tb">
                <Input
                  id="tb"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.thresholdB}
                  onChange={(e) => setField('thresholdB', e.target.value)}
                />
              </FormField>
              <FormField label="C ≥" htmlFor="tc">
                <Input
                  id="tc"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.thresholdC}
                  onChange={(e) => setField('thresholdC', e.target.value)}
                />
              </FormField>
              <FormField label="D ≥" htmlFor="td">
                <Input
                  id="td"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.thresholdD}
                  onChange={(e) => setField('thresholdD', e.target.value)}
                />
              </FormField>
            </div>
          </Card>

          {update.isError && (
            <div className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2">
              <p className="text-sm text-negative">
                Save failed: {(update.error as Error).message}
              </p>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 sticky bottom-0 bg-canvas/80 backdrop-blur-sm py-4 -mx-8 px-8 border-t border-border-subtle">
            <p className="text-sm text-fg-secondary">
              {savedFlash
                ? '✓ Saved. Version bumps when fee data changes.'
                : 'Saving rebumps version on data change.'}
            </p>
            <div className="flex gap-2">
              <Link
                to="/fee-profiles"
                className="text-sm font-medium text-fg-secondary hover:text-fg-primary"
              >
                Cancel
              </Link>
              <Button type="submit" loading={update.isPending}>
                Save changes
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
